import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Context Providers
import { EnvironmentProvider } from './context/EnvironmentContext';
import { AuthProvider } from './context/AuthContext';

// Pages
import DashboardPage from './pages/DashboardPage';
import NotFoundPage from './pages/NotFoundPage';
import UserDeletionPage from './pages/UserDeletionPage';
import FoldAccessPage from './pages/FoldAccessPage';
import PersonaSeedPage from './pages/PersonaSeedPage';
import LeakHunterPage from './pages/LeakHunterPage';
import CronJobsPage from './pages/CronJobsPage';
import RuntimeLimitsPage from './pages/RuntimeLimitsPage';
import FlockBotSpendPage from './pages/FlockBotSpendPage';
import RevenueOverviewPage from './pages/RevenueOverviewPage';
import TrialsPage from './pages/TrialsPage';

// Founder Dashboard
import FounderDashboardLayout from './pages/founder-dashboard/FounderDashboardLayout';
import SummaryPage from './pages/founder-dashboard/SummaryPage';
import EngagementPage from './pages/founder-dashboard/EngagementPage';
import OrganizationsPage from './pages/founder-dashboard/OrganizationsPage';
import OrganizationDetailPage from './pages/founder-dashboard/OrganizationDetailPage';
import GroupDetailPage from './pages/founder-dashboard/GroupDetailPage';
import MemberDetailPage from './pages/founder-dashboard/MemberDetailPage';
import GrowthPage from './pages/founder-dashboard/GrowthPage';
import RetentionPage from './pages/founder-dashboard/RetentionPage';
import RevenuePage from './pages/founder-dashboard/RevenuePage';
import InfrastructurePage from './pages/founder-dashboard/InfrastructurePage';
import AppUsagePage from './pages/founder-dashboard/AppUsagePage';

// Third-Party Analytics
import SendGridPage from './pages/SendGridPage';
import StripePage from './pages/StripePage';
import UserEngagementPage from './pages/UserEngagementPage';
import FirestorePage from './pages/FirestorePage';
import RekognitionPage from './pages/RekognitionPage';
import R2StoragePage from './pages/R2StoragePage';
import CompliancePage from './pages/CompliancePage';
import AlertsPage from './pages/AlertsPage';
import CasesPage from './pages/CasesPage';
import MemberReportsPage, { MemberReportPage } from './pages/MemberReportsPage';
import UserManagementPage from './pages/UserManagementPage';
import UserDetailPage from './pages/UserDetailPage';

// Onboarding Analytics
import OnboardingLayout from './pages/founder-dashboard/onboarding/OnboardingLayout';
import OnboardingFunnelsPage from './pages/founder-dashboard/onboarding/OnboardingFunnelsPage';
import OnboardingTrendsPage from './pages/founder-dashboard/onboarding/OnboardingTrendsPage';
import OnboardingOrgsPage from './pages/founder-dashboard/onboarding/OnboardingOrgsPage';
import OnboardingUsersPage from './pages/founder-dashboard/onboarding/OnboardingUsersPage';
import OnboardingStageDrillDown from './pages/founder-dashboard/onboarding/OnboardingStageDrillDown';
import OnboardingStepDrillDown from './pages/founder-dashboard/onboarding/OnboardingStepDrillDown';
import OnboardingUserDetailPage from './pages/founder-dashboard/onboarding/OnboardingUserDetailPage';
import OnboardingOrgDetailPage from './pages/founder-dashboard/onboarding/OnboardingOrgDetailPage';
import PersonalGroupFunnelPage from './pages/founder-dashboard/onboarding/PersonalGroupFunnelPage';
import PersonalGroupStageDrillDown from './pages/founder-dashboard/onboarding/PersonalGroupStageDrillDown';
import OnboardingFlowPage from './pages/founder-dashboard/onboarding/flow/OnboardingFlowPage';
import { FlowStepPage, FlowTimelinePage } from './pages/founder-dashboard/onboarding/flow/OnboardingFlowDrillDown';
import OutreachLayout from './pages/founder-dashboard/onboarding/outreach/OutreachLayout';
import OutreachOverviewPage from './pages/founder-dashboard/onboarding/outreach/OutreachOverviewPage';
import OutreachCampaignsPage from './pages/founder-dashboard/onboarding/outreach/OutreachCampaignsPage';
import OutreachTemplatesPage from './pages/founder-dashboard/onboarding/outreach/OutreachTemplatesPage';
import OutreachTemplateDetailPage from './pages/founder-dashboard/onboarding/outreach/OutreachTemplateDetailPage';
import OutreachQueuePage from './pages/founder-dashboard/onboarding/outreach/OutreachQueuePage';
import OutreachDeliveriesPage from './pages/founder-dashboard/onboarding/outreach/OutreachDeliveriesPage';
import OutreachSuppressionsPage from './pages/founder-dashboard/onboarding/outreach/OutreachSuppressionsPage';

// Church Demand Intelligence
import ChurchDemandLayout from './pages/founder-dashboard/church-demand/ChurchDemandLayout';
import ChurchDemandLanding from './pages/founder-dashboard/church-demand/ChurchDemandLanding';
import ClusterList from './pages/founder-dashboard/church-demand/ClusterList';
import ClusterDetail from './pages/founder-dashboard/church-demand/ClusterDetail';
import RawSearchStream from './pages/founder-dashboard/church-demand/RawSearchStream';
import DemandTrends from './pages/founder-dashboard/church-demand/DemandTrends';

// Feature Analytics
import FeatureAnalyticsLayout from './pages/feature-analytics/FeatureAnalyticsLayout';
import FeatureSummaryPage from './pages/feature-analytics/FeatureSummaryPage';
import PrayerAnalyticsPage from './pages/feature-analytics/PrayerAnalyticsPage';
import AttendanceAnalyticsPage from './pages/feature-analytics/AttendanceAnalyticsPage';
import EventsAnalyticsPage from './pages/feature-analytics/EventsAnalyticsPage';
import ChatAnalyticsPage from './pages/feature-analytics/ChatAnalyticsPage';

function App() {
  return (
    <EnvironmentProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {/* Main */}
              <Route index element={<DashboardPage />} />

              {/* Data Management */}
              <Route path="user-deletion" element={<UserDeletionPage />} />
              <Route path="access" element={<FoldAccessPage />} />
              {/* User Management */}
              <Route path="user-management" element={<UserManagementPage />} />
              <Route path="user-management/users/:id" element={<UserDetailPage />} />

              <Route path="persona-seed" element={<PersonaSeedPage />} />

              {/* Monitoring */}
              <Route path="leakhunter" element={<LeakHunterPage />} />
              <Route path="cron-jobs" element={<CronJobsPage />} />
              <Route path="limits" element={<RuntimeLimitsPage />} />
              <Route path="flockbot-spend" element={<FlockBotSpendPage />} />

              {/* Third-Party Analytics */}
              <Route path="sendgrid" element={<SendGridPage />} />
              <Route path="stripe" element={<StripePage />} />
              <Route path="user-engagement" element={<UserEngagementPage />} />
              <Route path="firestore" element={<FirestorePage />} />
              <Route path="rekognition" element={<RekognitionPage />} />
              <Route path="r2-storage" element={<R2StoragePage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="feedback" element={<CasesPage />} />
              <Route path="member-reports" element={<MemberReportsPage />} />
              <Route path="member-reports/:id" element={<MemberReportPage />} />

              {/* Combined Revenue (Stripe + Apple) */}
              <Route path="revenue" element={<RevenueOverviewPage />} />
              <Route path="trials" element={<TrialsPage />} />

              {/* Founder Dashboard */}
              <Route path="founder-dashboard" element={<FounderDashboardLayout />}>
                <Route index element={<SummaryPage />} />
                <Route path="summary" element={<SummaryPage />} />
                <Route path="engagement" element={<EngagementPage />} />
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="organizations/:id" element={<OrganizationDetailPage />} />
                <Route path="groups/:id" element={<GroupDetailPage />} />
                <Route path="members/:id" element={<MemberDetailPage />} />
                <Route path="growth" element={<GrowthPage />} />
                <Route path="retention" element={<RetentionPage />} />
                <Route path="revenue" element={<RevenuePage />} />
                <Route path="infrastructure" element={<InfrastructurePage />} />
                <Route
                  path="app-usage"
                  element={<Navigate to="/app-usage" replace />}
                />
              </Route>

              {/* App Usage (Analytics sidebar, sibling of Onboarding) */}
              <Route path="app-usage" element={<AppUsagePage />} />

              {/* Onboarding Analytics */}
              <Route path="onboarding" element={<OnboardingLayout />}>
                <Route index element={<OnboardingFunnelsPage />} />
                <Route path="funnels" element={<OnboardingFunnelsPage />} />
                <Route path="trends" element={<OnboardingTrendsPage />} />
                <Route path="orgs" element={<OnboardingOrgsPage />} />
                <Route path="users" element={<OnboardingUsersPage />} />
                <Route path="funnels/users/:stage" element={<OnboardingStageDrillDown />} />
                <Route path="funnels/orgs/:step" element={<OnboardingStepDrillDown />} />
                <Route path="users/:userId" element={<OnboardingUserDetailPage />} />
                <Route path="orgs/:orgId" element={<OnboardingOrgDetailPage />} />
                <Route path="personal-groups" element={<PersonalGroupFunnelPage />} />
                <Route path="personal-groups/users/:stage" element={<PersonalGroupStageDrillDown />} />
                <Route path="flow" element={<OnboardingFlowPage />} />
                <Route path="flow/steps/:step/:kind" element={<FlowStepPage />} />
                <Route path="flow/flows/:flowId" element={<FlowTimelinePage />} />
              </Route>

              {/* Lifecycle Outreach */}
              <Route path="outreach" element={<OutreachLayout />}>
                <Route index element={<OutreachOverviewPage />} />
                <Route path="campaigns" element={<OutreachCampaignsPage />} />
                <Route path="templates" element={<OutreachTemplatesPage />} />
                <Route
                  path="templates/:templateKey/:version"
                  element={<OutreachTemplateDetailPage />}
                />
                <Route path="queue" element={<OutreachQueuePage />} />
                <Route path="deliveries" element={<OutreachDeliveriesPage />} />
                <Route path="preferences" element={<OutreachSuppressionsPage />} />
              </Route>

              {/* Church Demand Intelligence */}
              <Route path="church-demand" element={<ChurchDemandLayout />}>
                <Route index element={<ChurchDemandLanding />} />
                <Route path="clusters" element={<ClusterList />} />
                <Route path="clusters/:clusterId" element={<ClusterDetail />} />
                <Route path="searches" element={<RawSearchStream />} />
                <Route path="trends" element={<DemandTrends />} />
              </Route>

              {/* Feature Analytics */}
              <Route path="feature-analytics" element={<FeatureAnalyticsLayout />}>
                <Route index element={<FeatureSummaryPage />} />
                <Route path="summary" element={<FeatureSummaryPage />} />
                <Route path="prayer" element={<PrayerAnalyticsPage />} />
                <Route path="attendance" element={<AttendanceAnalyticsPage />} />
                <Route path="events" element={<EventsAnalyticsPage />} />
                <Route path="chat" element={<ChatAnalyticsPage />} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </EnvironmentProvider>
  );
}

export default App;
